import { useEffect, useState } from "react";
import type { ChangeEvent, FC } from "react";
import { Button, Modal, TextInput, Label } from "@uhg-harmony/react";
import type { ModalProps } from "@uhg-harmony/react";
import styled from "styled-components";
import { labelDefaults } from "../ui/Label";

const labelProps = labelDefaults;

const BodyHeading = styled.h2`
  line-height: 120%;
  color: #0c55b8;
`;

const RootContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
  max-height: none;
  overflow: visible;
`;

type BaseTextInputModalProps = ModalProps & {
  open: boolean;
  onClose: () => void;
  onConfirm?: (inputValue: string) => void;
  templateName?: string;
  inputLabel: string;
  confirmButtonText: string;
  placeholder?: string;
  validateInput?: (inputValue: string) => string | null;
};

/**
 * Base modal component with text input field
 * Used by Move modal.
 */
export const BaseTextInputModal: FC<BaseTextInputModalProps> = ({
  title,
  open,
  onClose,
  onConfirm,
  templateName,
  inputLabel,
  confirmButtonText,
  placeholder = "",
  validateInput,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [validationError, setValidationError] = useState<string>("");
  const [hasInteracted, setHasInteracted] = useState(false);

  // Reset input whenever the modal reopens so stale text never persists
  useEffect(() => {
    if (open) {
      setInputValue("");
      setValidationError("");
      setHasInteracted(false);
    }
  }, [open]);

  const handleConfirm = () => {
    if (validateInput) {
      const error = validateInput(inputValue);
      if (error) {
        setValidationError(error);
        return;
      }
    }

    setValidationError("");
    onConfirm?.(inputValue);
    onClose();
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setInputValue(nextValue);
    setHasInteracted(true);

    if (validateInput) {
      const error = validateInput(nextValue);
      setValidationError(error || "");
    }
  };

  const handleInputBlur = () => {
    if (!validateInput) {
      return;
    }

    setHasInteracted(true);
    const error = validateInput(inputValue);
    setValidationError(error || "");
  };

  return (
    <>
      {open && (
        <Modal open={open} title={title} aria-label={typeof title === 'string' ? title : 'Dialog'} onClose={onClose}>
          <Modal.Body>
            <RootContainer>
              {templateName ? <BodyHeading>{templateName}</BodyHeading> : null}

              <Label {...labelProps}>{inputLabel}</Label>
              <div style={{ margin: "0 6px 0 4px" }}>
                <TextInput
                  required
                  width="1000px"
                  placeholder={placeholder}
                  value={inputValue}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                />
                {hasInteracted && validationError ? (
                  <div style={{ color: "#c62828", fontSize: "12px", marginTop: "6px" }}>
                    {validationError}
                  </div>
                ) : null}
              </div>
            </RootContainer>
          </Modal.Body>
          <Modal.Actions>
            <Button variant="secondary" onPress={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onPress={handleConfirm}>
              {confirmButtonText}
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </>
  );
};
